
import React, { useState } from 'react';
import { useToast } from '@/hooks/use-toast';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ArrowDown, DollarSign } from 'lucide-react';

interface WithdrawalRequestModalProps {
  open: boolean;
  onClose: () => void;
  availableBalance: number;
}

export const WithdrawalRequestModal: React.FC<WithdrawalRequestModalProps> = ({
  open,
  onClose,
  availableBalance,
}) => {
  const [amount, setAmount] = useState<number | ''>('');
  const [paymentMethod, setPaymentMethod] = useState('bank_transfer');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!amount || amount <= 0) {
      toast({
        variant: "destructive",
        title: "Invalid amount",
        description: "Please enter a valid withdrawal amount",
      });
      return;
    }
    
    if (amount > availableBalance) {
      toast({
        variant: "destructive",
        title: "Insufficient funds",
        description: "The requested amount exceeds your available balance",
      });
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // In a real app, this would be an API call
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      toast({
        title: "Withdrawal request submitted",
        description: `Your withdrawal request for $${amount.toFixed(2)} has been submitted successfully.`,
      });
      
      setAmount('');
      setPaymentMethod('bank_transfer');
      onClose();
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Request failed",
        description: "There was an error submitting your withdrawal request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>Request Withdrawal</DialogTitle>
          <DialogDescription>
            Enter the amount you would like to withdraw from your available balance of ${availableBalance.toFixed(2)}.
          </DialogDescription>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4 py-4">
          <div className="space-y-2">
            <Label htmlFor="amount">Withdrawal Amount</Label>
            <div className="relative">
              <DollarSign className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
              <Input
                id="amount"
                type="number"
                min={0}
                step="0.01"
                max={availableBalance}
                className="pl-9"
                value={amount}
                onChange={(e) => setAmount(e.target.value ? parseFloat(e.target.value) : '')}
                required
              />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="payment-method">Payment Method</Label>
            <Select value={paymentMethod} onValueChange={setPaymentMethod}>
              <SelectTrigger id="payment-method">
                <SelectValue placeholder="Select payment method" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                <SelectItem value="paypal">PayPal</SelectItem>
                <SelectItem value="check">Check</SelectItem>
              </SelectContent>
            </Select>
          </div>
          
          {paymentMethod === 'bank_transfer' && (
            <p className="text-sm text-muted-foreground">
              Funds will be transferred to your registered bank account within 3-5 business days.
            </p>
          )}
          
          {paymentMethod === 'paypal' && (
            <p className="text-sm text-muted-foreground">
              Funds will be transferred to your registered PayPal account within 1-2 business days.
            </p>
          )}
          
          {paymentMethod === 'check' && (
            <p className="text-sm text-muted-foreground">
              A check will be mailed to your registered address within 7-10 business days.
            </p>
          )}
          
          <DialogFooter>
            <Button variant="outline" type="button" onClick={onClose} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={isSubmitting || !amount || amount <= 0 || amount > availableBalance}>
              <ArrowDown className="mr-2 h-4 w-4" />
              {isSubmitting ? 'Processing...' : 'Request Withdrawal'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
